/*  Proposta C — Data Intelligence Workspace
    Dark, tactical, "sala di controllo". Inspired by Bloomberg · Vercel · Cron.
    Background #09090B, surface #121214, accent #3B82F6.
    Display: Clash Display · Body: Manrope.
    Floating sidebar glass + bento grid denso.
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { useV2Fonts, MOCK } from './_shared';
import {
  Search, Bell, Plus, Activity, AlertOctagon, ChevronRight,
  LayoutGrid, Ticket, BarChart3, Zap, Building2, Settings, ListChecks,
  TrendingUp, ArrowUpRight
} from 'lucide-react';

const TOKENS = {
  bg: '#09090B',
  surface: '#121214',
  surface2: '#1A1A1D',
  primary: '#FFFFFF',
  accent: '#3B82F6',
  accentGlow: 'rgba(59,130,246,0.18)',
  border: '#27272A',
  text: '#F8FAFC',
  muted: '#94A3B8',
  mutedDim: '#52525B',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  fontDisplay: '"Clash Display", "Space Grotesk", system-ui, sans-serif',
  fontBody: '"Manrope", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", "IBM Plex Mono", monospace',
};

const NAV = [
  { icon: LayoutGrid, label: 'Overview', active: true },
  { icon: Ticket, label: 'Tickets' },
  { icon: ListChecks, label: 'Board' },
  { icon: BarChart3, label: 'Analytics' },
  { icon: Zap, label: 'Energy' },
  { icon: Building2, label: 'Hotels' },
  { icon: Settings, label: 'System' },
];

const PRIORITY_BADGE = {
  urgente: { bg: 'rgba(239,68,68,0.12)', fg: TOKENS.danger, label: 'CRIT' },
  alta: { bg: 'rgba(245,158,11,0.12)', fg: TOKENS.warning, label: 'HIGH' },
  media: { bg: 'rgba(148,163,184,0.12)', fg: TOKENS.muted, label: 'MED' },
};

const Sparkline = ({ data, color = TOKENS.accent, height = 32, width = 140 }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 6) - 3}`).join(' ');
  const lastY = height - ((data[data.length - 1] - min) / range) * (height - 6) - 3;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        fill="url(#sparkfill)"
        points={`0,${height} ${pts} ${width},${height}`}
      />
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
      <circle cx={width} cy={lastY} r="3" fill={color} />
      <circle cx={width} cy={lastY} r="6" fill={color} opacity="0.25" />
    </svg>
  );
};

const ProposalC = () => {
  useV2Fonts();

  return (
    <div
      className="min-h-screen flex"
      style={{ background: TOKENS.bg, fontFamily: TOKENS.fontBody, color: TOKENS.text }}
      data-testid="proposal-c"
    >
      {/* Floating glass sidebar */}
      <aside
        className="w-16 border-r flex flex-col py-4"
        style={{
          background: 'rgba(18,18,20,0.6)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderColor: TOKENS.border,
        }}
      >
        <div className="px-3 mb-5">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center font-bold"
            style={{
              background: `linear-gradient(135deg, ${TOKENS.accent}, #1d4ed8)`,
              fontFamily: TOKENS.fontDisplay,
              boxShadow: `0 0 24px ${TOKENS.accentGlow}`,
            }}
          >
            HF
          </div>
        </div>
        <nav className="flex-1 space-y-1.5 px-3">
          {NAV.map((n, i) => (
            <button
              key={i}
              className="w-10 h-10 rounded-lg flex items-center justify-center relative"
              style={{
                background: n.active ? TOKENS.surface2 : 'transparent',
                color: n.active ? TOKENS.text : TOKENS.mutedDim,
                border: n.active ? `1px solid ${TOKENS.border}` : '1px solid transparent',
              }}
            >
              <n.icon className="w-4 h-4" />
              {n.active && (
                <span
                  className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r"
                  style={{ background: TOKENS.accent, boxShadow: `0 0 12px ${TOKENS.accent}` }}
                />
              )}
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b flex items-center px-6 gap-4" style={{ borderColor: TOKENS.border }}>
          <div className="flex items-center gap-2 text-xs" style={{ fontFamily: TOKENS.fontMono, color: TOKENS.muted }}>
            <Activity className="w-3.5 h-3.5" style={{ color: TOKENS.success }} />
            <span>SYSTEM</span>
            <span style={{ color: TOKENS.success }}>● LIVE</span>
            <span style={{ color: TOKENS.mutedDim }}>·</span>
            <span>{MOCK.hotelName.toUpperCase()}</span>
            <span style={{ color: TOKENS.mutedDim }}>·</span>
            <span>UPTIME 99.8%</span>
          </div>
          <div className="flex-1 max-w-md">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-md"
              style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
            >
              <Search className="w-3.5 h-3.5" style={{ color: TOKENS.mutedDim }} />
              <input
                placeholder="$ search tickets, assets, rooms..."
                className="flex-1 bg-transparent text-xs outline-none"
                style={{ fontFamily: TOKENS.fontMono, color: TOKENS.text }}
              />
              <kbd
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: TOKENS.surface2, color: TOKENS.muted, fontFamily: TOKENS.fontMono }}
              >
                ⌘K
              </kbd>
            </div>
          </div>
          <button
            className="relative p-2 rounded-md"
            style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
          >
            <Bell className="w-3.5 h-3.5" />
            <span
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full"
              style={{ background: TOKENS.danger, boxShadow: `0 0 8px ${TOKENS.danger}` }}
            />
          </button>
          <button
            className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{
              background: TOKENS.accent,
              color: '#fff',
              fontFamily: TOKENS.fontMono,
              boxShadow: `0 0 24px ${TOKENS.accentGlow}`,
            }}
          >
            <Plus className="w-3.5 h-3.5" /> NEW
          </button>
          <Link to="/mockup/v2" className="text-xs hover:text-white" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
            ← COMPARE
          </Link>
        </header>

        <main className="flex-1 overflow-auto p-6 space-y-4">
          {/* Top KPI strip */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { id: 'KPI_01', label: 'OPEN_TICKETS', value: MOCK.kpis.open, delta: '+12%', spark: [42, 40, 43, 45, 44, 48, 45], color: TOKENS.warning },
              { id: 'KPI_02', label: 'AVG_RESOLUTION', value: MOCK.kpis.avgResolution, delta: '−18m', spark: [3.1, 2.9, 2.8, 2.7, 2.6, 2.5, 2.4], color: TOKENS.success },
              { id: 'KPI_03', label: 'SLA_COMPLIANCE', value: MOCK.kpis.sla, delta: '+2pp', spark: [94, 95, 96, 96, 97, 97, 98], color: TOKENS.success },
              { id: 'KPI_04', label: 'ENERGY_IDX', value: MOCK.consumption.efficiency, delta: '+5pt', spark: MOCK.consumption.trend12m, color: TOKENS.accent },
            ].map((k) => (
              <div
                key={k.id}
                className="rounded-lg p-4 relative overflow-hidden"
                style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="text-[10px] tracking-widest"
                    style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}
                  >
                    {k.id} · {k.label}
                  </div>
                  <ArrowUpRight className="w-3 h-3" style={{ color: TOKENS.mutedDim }} />
                </div>
                <div className="flex items-end justify-between">
                  <div
                    className="text-4xl font-bold tracking-tight tabular-nums"
                    style={{ fontFamily: TOKENS.fontDisplay }}
                  >
                    {k.value}
                  </div>
                  <Sparkline data={k.spark} color={k.color} />
                </div>
                <div
                  className="mt-2 text-[10px]"
                  style={{ color: k.color, fontFamily: TOKENS.fontMono }}
                >
                  Δ {k.delta} · 7d
                </div>
              </div>
            ))}
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-12 gap-4">
            {/* Alert stream — 8 col */}
            <section
              className="col-span-8 rounded-lg overflow-hidden"
              style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
            >
              <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: TOKENS.border }}>
                <div className="flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4" style={{ color: TOKENS.danger }} />
                  <h2 className="text-sm font-semibold" style={{ fontFamily: TOKENS.fontDisplay }}>
                    PRIORITY STREAM
                  </h2>
                  <span
                    className="text-[10px] px-2 py-0.5 rounded"
                    style={{
                      background: 'rgba(239,68,68,0.12)',
                      color: TOKENS.danger,
                      fontFamily: TOKENS.fontMono,
                    }}
                  >
                    {MOCK.urgent.length} ACTIVE
                  </span>
                </div>
                <button className="text-xs flex items-center gap-1" style={{ color: TOKENS.accent, fontFamily: TOKENS.fontMono }}>
                  VIEW_ALL <ChevronRight className="w-3 h-3" />
                </button>
              </div>

              <div>
                {MOCK.urgent.map((t, idx) => {
                  const p = PRIORITY_BADGE[t.priority];
                  return (
                    <div
                      key={t.id}
                      className="flex items-center gap-4 px-5 py-3 text-sm cursor-pointer hover:bg-white/[0.02]"
                      style={{ borderBottom: idx < MOCK.urgent.length - 1 ? `1px solid ${TOKENS.border}` : 'none' }}
                    >
                      <span
                        className="text-xs tabular-nums w-14"
                        style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}
                      >
                        {t.id}
                      </span>
                      <span
                        className="text-[10px] px-2 py-0.5 rounded font-bold w-12 text-center"
                        style={{ background: p.bg, color: p.fg, fontFamily: TOKENS.fontMono }}
                      >
                        {p.label}
                      </span>
                      <span className="flex-1">{t.title}</span>
                      <span className="text-xs" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                        room/{t.room}
                      </span>
                      <span
                        className="w-16 text-right text-xs tabular-nums"
                        style={{ color: TOKENS.warning, fontFamily: TOKENS.fontMono }}
                      >
                        {t.age}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* System heatmap — 4 col */}
            <section
              className="col-span-4 rounded-lg p-5"
              style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
            >
              <div className="text-[10px] tracking-widest mb-1" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                MAP_01 · ISSUE_HEATMAP
              </div>
              <h2 className="text-sm font-semibold mb-4" style={{ fontFamily: TOKENS.fontDisplay }}>
                Concentrazione problemi
              </h2>
              <div className="grid grid-cols-6 gap-1.5">
                {Array.from({ length: 36 }).map((_, i) => {
                  // Pattern deterministico (no Math.random — render puro)
                  const v = ((i * 37 + 13) % 100) / 100;
                  const intensity = v < 0.4 ? 0.08 : v < 0.7 ? 0.25 : v < 0.9 ? 0.55 : 0.9;
                  const color = v > 0.85 ? TOKENS.danger : v > 0.65 ? TOKENS.warning : TOKENS.accent;
                  return (
                    <div
                      key={i}
                      className="aspect-square rounded-sm"
                      style={{
                        background: color,
                        opacity: intensity,
                      }}
                    />
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-between text-[10px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                <span>FLOOR_-1</span>
                <div className="flex gap-1.5 items-center">
                  <span>LOW</span>
                  <div className="flex gap-0.5">
                    {[0.1, 0.3, 0.55, 0.85].map((o, i) => (
                      <div key={i} className="w-3 h-2 rounded-sm" style={{ background: TOKENS.accent, opacity: o }} />
                    ))}
                  </div>
                  <span>HIGH</span>
                </div>
                <span>FLOOR_6</span>
              </div>
            </section>

            {/* Energy chart — 7 col */}
            <section
              className="col-span-7 rounded-lg p-5"
              style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-widest" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                    CHART_02 · ENERGY_EFF_12M
                  </div>
                  <h2 className="text-sm font-semibold" style={{ fontFamily: TOKENS.fontDisplay }}>
                    Trend efficienza energetica
                  </h2>
                </div>
                <div className="flex gap-4 text-xs">
                  <div className="text-right">
                    <div className="text-[10px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>kWh/cam</div>
                    <div className="font-bold tabular-nums" style={{ fontFamily: TOKENS.fontDisplay }}>{MOCK.consumption.kwhPerRoom}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>m³/cam</div>
                    <div className="font-bold tabular-nums" style={{ fontFamily: TOKENS.fontDisplay }}>{MOCK.consumption.waterPerRoom}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>Smc/cam</div>
                    <div className="font-bold tabular-nums" style={{ fontFamily: TOKENS.fontDisplay }}>{MOCK.consumption.smcPerRoom}</div>
                  </div>
                </div>
              </div>
              <div className="h-32 flex items-end gap-1.5">
                {MOCK.consumption.trend12m.map((v, i) => {
                  const max = Math.max(...MOCK.consumption.trend12m);
                  const h = (v / max) * 100;
                  const isLast = i === 11;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                      <div className="text-[9px] tabular-nums" style={{ color: isLast ? TOKENS.accent : TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                        {v}
                      </div>
                      <div
                        className="w-full rounded-t"
                        style={{
                          height: `${h}%`,
                          background: isLast ? TOKENS.accent : 'rgba(59,130,246,0.35)',
                          boxShadow: isLast ? `0 0 12px ${TOKENS.accentGlow}` : 'none',
                          minHeight: 4,
                        }}
                      />
                      <div className="text-[9px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                        {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'][i]}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Team load — 5 col */}
            <section
              className="col-span-5 rounded-lg p-5"
              style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
            >
              <div className="text-[10px] tracking-widest mb-1" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                TABLE_03 · TEAM_LOAD
              </div>
              <h2 className="text-sm font-semibold mb-4" style={{ fontFamily: TOKENS.fontDisplay }}>
                Tecnici · carico attuale
              </h2>
              {MOCK.technicians.map((t) => {
                const pct = Math.min(100, t.open * 14);
                const overload = t.open >= 7;
                return (
                  <div key={t.name} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-medium">{t.name}</span>
                      <span style={{ color: overload ? TOKENS.danger : TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                        {t.open} open · {t.today} today
                      </span>
                    </div>
                    <div className="h-1 rounded-full" style={{ background: TOKENS.surface2 }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: overload ? TOKENS.danger : TOKENS.accent,
                          boxShadow: overload ? `0 0 6px ${TOKENS.danger}` : `0 0 6px ${TOKENS.accentGlow}`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </section>

            {/* Hotels group strip — full */}
            <section
              className="col-span-12 rounded-lg p-5"
              style={{ background: TOKENS.surface, border: `1px solid ${TOKENS.border}` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-widest" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                    GROUP_04 · MULTI_PROPERTY
                  </div>
                  <h2 className="text-sm font-semibold" style={{ fontFamily: TOKENS.fontDisplay }}>
                    Vista gruppo
                  </h2>
                </div>
                <TrendingUp className="w-4 h-4" style={{ color: TOKENS.muted }} />
              </div>
              <div className="grid grid-cols-4 gap-3">
                {MOCK.hotels.map((h) => (
                  <div
                    key={h.name}
                    className="rounded-md p-4 relative overflow-hidden"
                    style={{ background: TOKENS.surface2, border: `1px solid ${TOKENS.border}` }}
                  >
                    <div className="text-sm font-semibold mb-1" style={{ fontFamily: TOKENS.fontDisplay }}>
                      {h.name}
                    </div>
                    <div className="flex items-baseline gap-5 mt-2">
                      <div>
                        <div className="text-[9px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                          TICKETS
                        </div>
                        <div className="text-xl font-bold tabular-nums" style={{ fontFamily: TOKENS.fontDisplay }}>
                          {h.open}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px]" style={{ color: TOKENS.mutedDim, fontFamily: TOKENS.fontMono }}>
                          ENERGY_IDX
                        </div>
                        <div
                          className="text-xl font-bold tabular-nums"
                          style={{
                            fontFamily: TOKENS.fontDisplay,
                            color: h.eff >= 65 ? TOKENS.success : TOKENS.warning,
                          }}
                        >
                          {h.eff}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProposalC;
